/**
 * Composite Analyzer: EMA Proximity Intelligence — ATM Scope
 *
 * Per-ATM adaptation of emaProximityIntelligence. Where the setup-scope
 * version asks "which EMA does this SETUP win on as a pullback edge?",
 * this asks the same question of the management style:
 *
 *   1. Does THIS ATM's bracket structure (BE, trail, target placement)
 *      tend to perform best when the ENTRY happened at a specific EMA?
 *      A BE-then-trail ATM that wins 76% on 21-EMA pullbacks but 30% on
 *      extended entries is telling you the management is calibrated to
 *      pullback-style entries — chasing breaks the math.
 *
 *   2. Does THIS ATM struggle disproportionately with extended entries?
 *      Same chase-penalty pattern as the setup version, but framed
 *      around the ATM: "the trailing logic doesn't have time to work
 *      when you enter late."
 *
 * Reuses the core helpers (`classifyEmaProximity`, etc.) from
 * emaProximityIntelligence so thresholds stay consistent across scopes
 * and we don't duplicate the EMA-distance math.
 *
 * Reads:
 *   trade.barAnalysis.emasAtEntry, trade.entry, trade.stopLoss(Points),
 *   trade.netPnl, trade.atmStrategy
 */

import { CATEGORY, SEVERITY, makeObservation, makeAction, ACTION_TYPE } from '../../types.js';
import {
  classifyEmaProximity,
  PULLBACK_NEAR,
  CHASE_THRESHOLD,
  MIN_TRADES_PER_BUCKET,
  PROFILE_MIN_GAP,
  CHASE_MIN_GAP,
} from './emaProximityIntelligence.js';

export const key = 'emaProximityAtm';
export const tier = 'free';

const fmtPct = (n) => `${Math.round(n * 100)}%`;

// Task A.2 — templateType-aware action language. The existing "the ATM's
// BE-and-trail logic needs continuation that chase entries don't get"
// framing assumes the ATM trails. A TP-only ATM with a fixed target doesn't
// have trail logic at all — its chase penalty is geometric (the static
// target is closer to a chase entry than to a pullback entry) rather than
// mechanical (a trail can't fire on a chase). Different cause → different
// fix → different action text. Hybrid lives between but mostly reads as AT
// for action language since at least one bracket trails.
const isTrailingType = (templateType) => templateType === 'AT' || templateType === 'Hybrid';

// ── Pattern 1: ATM's preferred-EMA pullback profile ──────────────────────

const detectAtmPullbackProfile = (atmName, trades, templateType) => {
  const byPeriod = new Map();
  let extendedCount = 0;
  let total = 0;
  for (const t of trades) {
    const p = classifyEmaProximity(t);
    if (!p) continue;
    total++;
    if (p.extended) { extendedCount++; continue; }
    if (p.closestDistStops > PULLBACK_NEAR) continue;
    if (!byPeriod.has(p.closestPeriod)) byPeriod.set(p.closestPeriod, []);
    byPeriod.get(p.closestPeriod).push(t);
  }
  if (byPeriod.size === 0) return null;

  const stats = [];
  for (const [period, group] of byPeriod) {
    if (group.length < MIN_TRADES_PER_BUCKET) continue;
    const wins = group.filter(t => Number.isFinite(t.netPnl) && t.netPnl > 0).length;
    stats.push({ period, count: group.length, wins, wr: wins / group.length });
  }
  if (stats.length === 0) return null;

  stats.sort((a, b) => (b.wr - a.wr) || (b.count - a.count));
  const best = stats[0];
  if (best.wr < 0.55) return null;

  const others = stats.slice(1);
  let othersAvgWr = null;
  if (others.length > 0) {
    const totalCount = others.reduce((s, x) => s + x.count, 0);
    const weightedWins = others.reduce((s, x) => s + x.wins, 0);
    othersAvgWr = totalCount > 0 ? weightedWins / totalCount : null;
    if (othersAvgWr != null && (best.wr - othersAvgWr) < PROFILE_MIN_GAP) return null;
  }

  // Task A.2 — detail varies subtly by management style. A trailing ATM's
  // pullback dependency reads as a TRAIL-geometry calibration ("BE/trail/
  // target math is calibrated"); a TP ATM's reads as a TARGET-geometry one
  // ("the static target sits at the distance a pullback entry can reach").
  const trail = isTrailingType(templateType);
  const detailMechanic = trail
    ? `The ATM's BE/trail/target math is calibrated to that pullback distance — different entries change the geometry.`
    : `The static target sits at the R-distance a pullback entry can actually reach. Chase entries shorten the runway to that same target.`;
  return makeObservation({
    id: `${key}:${atmName}:pullback-profile:${best.period}`,
    analyzer: key,
    scope: 'atm',
    scopeId: atmName,
    severity: SEVERITY.SUCCESS,
    category: CATEGORY.SETUP,
    title: `"${atmName}" works best when the entry is a ${best.period}-EMA pullback (${fmtPct(best.wr)} WR)`,
    detail: othersAvgWr != null
      ? `${best.wins}/${best.count} winners when entry is within ${PULLBACK_NEAR.toFixed(1)} stops of the ${best.period} EMA on this ATM. Other-EMA pullbacks: ${fmtPct(othersAvgWr)} WR. ${detailMechanic}`
      : `${best.wins}/${best.count} winners when entry is within ${PULLBACK_NEAR.toFixed(1)} stops of the ${best.period} EMA. The ATM's bracket structure rewards this entry distance.`,
    action: `Pair this ATM with setups whose entry trigger is the ${best.period} EMA. Mismatched-EMA entries on this ATM aren't the same trade.`,
    evidence: {
      bestPeriod: best.period, bestWr: best.wr, bestCount: best.count,
      othersAvgWr, perPeriod: stats, extendedCount, total,
      templateType: templateType || null,  // Task A.2
    },
    confidence: 0.8,
    priority: 78,
  });
};

// ── Pattern 2: ATM-specific chase penalty ────────────────────────────────

const detectAtmChasePenalty = (atmName, trades, templateType) => {
  const near = [];
  const extended = [];
  for (const t of trades) {
    const p = classifyEmaProximity(t);
    if (!p) continue;
    if (p.extended) extended.push(t);
    else if (p.closestDistStops <= PULLBACK_NEAR) near.push(t);
  }
  if (near.length < MIN_TRADES_PER_BUCKET || extended.length < MIN_TRADES_PER_BUCKET) return null;

  const wr = (group) => group.filter(t => Number.isFinite(t.netPnl) && t.netPnl > 0).length / group.length;
  const nearWr = wr(near);
  const extendedWr = wr(extended);
  const gap = nearWr - extendedWr;
  if (gap < CHASE_MIN_GAP) return null;

  let worstExtended = null; let worstNet = Infinity;
  for (const t of extended) {
    if (Number.isFinite(t.netPnl) && t.netPnl < worstNet) {
      worstNet = t.netPnl; worstExtended = t;
    }
  }
  const jumpAction = worstExtended?.id
    ? [makeAction({
        type: ACTION_TYPE.JUMP_TO_TRADE,
        label: 'Show worst chase',
        payload: { tradeId: worstExtended.id, date: worstExtended.date || null },
        doneLabel: 'Jumping…',
      })]
    : null;

  // Task A.2 — chase-penalty cause + fix wording adapts by templateType.
  // AT/Hybrid: trail logic literally can't fire because there's no
  //            pullback continuation. Fix is a different ATM with wider
  //            stop + fixed targets.
  // TP: the static target is geometrically closer to a chase entry, so
  //     the chase trade reaches the target less often. Fix is the same
  //     ATM with a recalibrated R-target sized for chase-entry geometry,
  //     OR a different ATM entirely. We surface the latter as a softer
  //     suggestion (the trader's R-math is their call).
  const trail = isTrailingType(templateType);
  const causeMechanic = trail
    ? `The ATM's BE-and-trail logic needs continuation that chase entries don't get.`
    : `The fixed R-target sits at a distance that a measured pullback can reach; a chase entry gives up that runway.`;
  const fixSuggestion = trail
    ? `Use a different ATM for chase entries — wider stop, fixed targets that don't depend on a clean trail. This ATM's geometry assumes a measured pullback.`
    : `For chase entries, either pair them with a wider-R ATM that has room for the shorter runway, or pass on the entry. This ATM's static target was sized for the pullback's geometry, not the chase's.`;
  return makeObservation({
    id: `${key}:${atmName}:chase-penalty`,
    analyzer: key,
    scope: 'atm',
    scopeId: atmName,
    severity: SEVERITY.WARNING,
    category: CATEGORY.EXECUTION,
    title: `"${atmName}" extended entries lose ${fmtPct(gap)} more — the ATM can't save a chase`,
    detail: `Within ${PULLBACK_NEAR.toFixed(1)} stops of an EMA: ${fmtPct(nearWr)} WR (${near.length} trades). Beyond ${CHASE_THRESHOLD.toFixed(1)} stops (extended): ${fmtPct(extendedWr)} WR (${extended.length} trades). ${causeMechanic}`,
    action: fixSuggestion,
    actions: jumpAction,
    evidence: {
      nearCount: near.length, extendedCount: extended.length,
      nearWr, extendedWr, gap, worstTradeId: worstExtended?.id ?? null,
      templateType: templateType || null,  // Task A.2
    },
    confidence: 0.8,
    priority: 78,
  });
};

// ── Public analyze() ─────────────────────────────────────────────────────

export const analyze = (ctx) => {
  const { atmName, atmTrades, ntTemplateVersions } = ctx || {};
  if (!atmName || !Array.isArray(atmTrades) || atmTrades.length === 0) return null;

  // Live-trade gate: pullback-profile and chase-penalty both bucket trades
  // by outcome (per-EMA WR, near vs extended WR). Open trades have unrealized
  // P&L which isn't a realized outcome — including them fires premature
  // "this ATM is failing on chases" claims while the user is still in the
  // trade (panic-exit risk). Matches summarizePnl semantics.
  const closedAtmTrades = atmTrades.filter(t => t?.isOpen !== true);
  if (closedAtmTrades.length === 0) return null;

  // Task A.2 — read templateType from the newest version snapshot. Newest
  // because the chase-penalty / pullback-profile insights describe the
  // current management style, not historical iterations. Null-safe: ATMs
  // without parsed XML still produce observations using the legacy (AT)
  // language since trail-style ATMs are the common case here.
  const templateType = ntTemplateVersions?.[atmName]?.[0]?.snapshot?.templateType || null;

  const out = [];
  const profile = detectAtmPullbackProfile(atmName, closedAtmTrades, templateType);
  if (profile) out.push(profile);
  const chase = detectAtmChasePenalty(atmName, closedAtmTrades, templateType);
  if (chase) out.push(chase);
  return out.length === 0 ? null : out;
};

// Exports for tests
export { detectAtmPullbackProfile, detectAtmChasePenalty, isTrailingType };
